import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Calendar, Clock, Users, ArrowRight, User, Star, Flame, Archive } from 'lucide-react';
import { studyGroups } from '../data/studyGroups';

// Re-export for detail page
export { studyGroups };

// Filter only upcoming study groups
const upcomingGroups = studyGroups.filter(g => g.status !== 'archived');
const allTags = [...new Set(upcomingGroups.flatMap(g => g.tags))];

const StudyGroupsPage = () => {
  const [selectedTags, setSelectedTags] = useState([]);

  const toggleTag = (tag) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const filteredGroups = selectedTags.length === 0 ? upcomingGroups : upcomingGroups.filter(g => g.tags.some(tag => selectedTags.includes(tag)));

  return (
    <div className="min-h-screen pt-20" data-testid="study-groups-page">
      {/* Hero */}
      <section className="py-12 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="hero-glow top-0 left-1/4" />
        <div className="container-main relative z-10">
          <div className="max-w-3xl">
            <Link to="/services" className="inline-flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--primary)] mb-6 font-mono text-sm">
              <ArrowRight className="w-4 h-4 rotate-180" /> Back to Services
            </Link>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 border border-[var(--accent-cyan)] bg-[var(--accent-cyan)]/10 flex items-center justify-center">
                <BookOpen className="w-8 h-8 text-[var(--accent-cyan)]" />
              </div>
              <div>
                <p className="font-mono text-sm uppercase tracking-wider text-[var(--accent-cyan)]">Community Learning</p>
                <h1 className="text-3xl md:text-5xl font-bold text-[var(--text-main)]">Study Groups</h1>
              </div>
            </div>
            <p className="text-[var(--text-muted)] text-lg md:text-xl leading-relaxed">
              Join focused study groups led by mentors to deepen your expertise in specific cybersecurity domains through collaborative peer learning.
            </p>
          </div>
        </div>
      </section>

      {upcomingGroups.length === 0 ? (
        <section className="py-16 md:py-24 relative overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-30" />
          <div className="hero-glow top-0 right-1/4" />
          <div className="container-main relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="bg-[var(--surface)] border border-[var(--border)] p-8 md:p-12">
                <div className="space-y-6">
                  <p className="text-[var(--accent-cyan)] text-sm font-mono uppercase tracking-wider">Community Learning</p>
                  <h2 className="text-3xl md:text-4xl font-bold text-[var(--text-main)]">Study Groups Coming Soon</h2>
                  <p className="text-[var(--text-muted)] text-lg leading-relaxed">We're organizing focused study groups led by mentors to help you deepen your expertise in specific cybersecurity domains through collaborative peer learning.</p>
                  <p className="text-[var(--text-muted)] text-base">Check back soon for announcements on the next batch of study groups.</p>
                </div>
              </div>
              <div className="bg-[var(--surface-highlight)] border border-[var(--border)] border-dashed p-8 md:p-12 flex flex-col items-center justify-center text-center space-y-4">
                <div className="text-5xl">📚</div>
                <p className="text-[var(--text-muted)] text-lg">Study groups arriving soon</p>
                <p className="text-sm text-[var(--text-muted)]">Follow us for updates on study group schedules and mentor announcements.</p>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <>
          {/* Filter */}
          <section className="py-8 border-y border-[var(--border)] ">
            <div className="container-main">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <span className="font-mono text-sm text-[var(--accent-cyan)] uppercase tracking-wider">Filter by Tag:</span>
                <div className="flex flex-wrap gap-2">
                  {allTags.map((tag) => (
                    <button key={tag} onClick={() => toggleTag(tag)} className={`px-4 py-2 font-mono text-sm border transition-colors duration-200 ${selectedTags.includes(tag) ? 'border-[var(--accent-cyan)] bg-[var(--accent-cyan)]/20 text-[var(--accent-cyan)]' : 'border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--accent-cyan)]/50'}`} data-testid={`tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}>{tag}</button>
                  ))}
                  {selectedTags.length > 0 && (
                    <button onClick={() => setSelectedTags([])} className="px-4 py-2 font-mono text-sm border border-[var(--error)]/50 text-[var(--error)] hover:bg-[var(--error)]/10">Clear All</button>
                  )}
                </div>
              </div>
              <p className="mt-4 text-sm text-[var(--text-muted)]">Showing {filteredGroups.length} of {upcomingGroups.length} study groups</p>
            </div>
          </section>

          {/* Study Groups List */}
          <section className="py-16">
            <div className="container-main space-y-8">
              {filteredGroups.map((group) => (
                <div key={group.id} className="bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--accent-cyan)] transition-colors duration-300 overflow-hidden relative" data-testid={`study-group-card-${group.id}`}>
                  {/* Featured & Popular Badges */}
                  <div className="absolute top-4 right-4 flex gap-2 z-10">
                    {group.featured && (
                      <span className="flex items-center gap-1 px-2 py-1 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] text-xs font-mono uppercase" data-testid={`featured-badge-${group.id}`}>
                        <Star className="w-3 h-3 fill-[#FFD700]" /> Featured
                      </span>
                    )}
                    {group.popular && (
                      <span className="flex items-center gap-1 px-2 py-1 bg-[#FF6B00]/20 border border-[#FF6B00] text-[#FF6B00] text-xs font-mono uppercase" data-testid={`popular-badge-${group.id}`}>
                        <Flame className="w-3 h-3" /> Popular
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-3">
                    <div className="lg:col-span-2 p-8 border-b lg:border-b-0 lg:border-r border-[var(--border)]">
                      <div className="flex items-center gap-3 mb-3">
                        <span className={`px-3 py-1 text-xs font-mono uppercase ${group.status === 'upcoming' ? 'bg-[var(--primary)]/20 text-[var(--primary)]' : 'bg-[var(--text-muted)]/20 text-[var(--text-muted)]'}`}>{group.status === 'upcoming' ? 'Coming Soon' : group.status}</span>
                        <span className="px-3 py-1 bg-[var(--accent-cyan)]/20 text-[var(--accent-cyan)] text-xs font-mono uppercase">{group.level}</span>
                      </div>
                      <h3 className="text-2xl font-bold text-[var(--text-main)] mb-2">{group.title}</h3>
                      <p className="text-[var(--text-muted)] leading-relaxed mb-6">{group.description}</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[var(--accent-cyan)]" /><div><p className="text-xs text-[var(--text-muted)]">Starts</p><p className="text-sm text-[var(--text-main)] font-mono">{group.startDate}</p></div></div>
                        <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[var(--accent-cyan)]" /><div><p className="text-xs text-[var(--text-muted)]">Duration</p><p className="text-sm text-[var(--text-main)] font-mono">{group.duration}</p></div></div>
                        <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[var(--accent-cyan)]" /><div><p className="text-xs text-[var(--text-muted)]">Spots Left</p><p className="text-sm text-[var(--text-main)] font-mono">{group.spotsLeft}/{group.groupSize}</p></div></div>
                        <div className="flex items-center gap-2"><User className="w-4 h-4 text-[var(--accent-cyan)]" /><div><p className="text-xs text-[var(--text-muted)]">Mentor</p><p className="text-sm text-[var(--text-main)] font-mono">{group.mentor}</p></div></div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {group.tags.map((tag, idx) => (<span key={idx} className="px-2 py-1 bg-[var(--surface-highlight)] border border-[var(--border)] text-xs text-[var(--text-muted)] font-mono">{tag}</span>))}
                      </div>
                    </div>
                    <div className="p-8 flex flex-col">
                      <div className="mb-6 mt-8">
                        <p className="text-xs text-[var(--text-muted)] mb-1">Enrollment</p>
                        <p className="font-mono text-4xl font-bold text-[var(--accent-cyan)]">FREE</p>
                        <p className="text-xs text-[var(--text-muted)]">Limited spots available</p>
                      </div>
                      <div className="mt-auto space-y-3">
                        <Link to={`/services/study-groups/${group.id}`} className="w-full bg-[var(--primary)] text-black font-mono font-bold px-6 py-4 uppercase tracking-widest hover:bg-[var(--primary-dim)] transition-colors duration-200 shadow-[0_0_15px_var(--glow-primary)] text-center block" data-testid={`know-more-btn-${group.id}`}>Know More</Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {filteredGroups.length === 0 && (
                <div className="text-center py-16">
                  <p className="text-[var(--text-muted)] text-lg">No study groups match the selected filters.</p>
                  <button onClick={() => setSelectedTags([])} className="mt-4 text-[var(--accent-cyan)] font-mono hover:underline">Clear filters</button>
                </div>
              )}
            </div>
          </section>
        </>
      )}
      <div className="container-main flex justify-center mb-12">
        <Link to="/services/study-groups/archive" className="inline-flex items-center gap-2 mt-6 text-[var(--text-muted)] hover:text-[var(--accent-purple)] font-mono text-sm border border-[var(--border)] px-4 py-2 hover:border-[var(--accent-purple)] transition-colors" data-testid="view-archive-link">
          <Archive className="w-4 h-4" /> View Past Groups
        </Link>
      </div>
    </div>
  );
};

export default StudyGroupsPage;
